import React, { memo, useCallback, useMemo } from 'react';

import PropTypes from 'prop-types';
import { TextField, InputControlLabel, Select } from '@picsio/ui';

import localization from '../../shared/strings';
import { useLocalFieldValue, useBlurCommit } from './hooks';
import DetailsBlock from '../DetailsBlock';
import InventoryTextField from './Inventory/InventoryTextField';
import Tooltip from '../Tooltip/Tooltip';
import ReadOnlyFieldTooltip from './helpers/ReadOnlyFieldTooltip';
import { buildWeightSelectOptions, normalizeWeightUnit, getUnitDisplayLabel } from './helpers';
import buildComplianceFieldLabel from './helpers/buildComplianceFieldLabel';
import {
  normalizeCountryCodeOfOrigin,
  validateCountryCodeOfOrigin,
  normalizeHarmonizedSystemCode,
  validateHarmonizedSystemCode,
} from './helpers/validateComplianceFields';
import s from './styles.module.scss';

const l10n = localization.PRODUCT_INFO.inventory;

const validateCountryField = (value) => validateCountryCodeOfOrigin(value, {
  invalidMessage: l10n.countryCodeOfOriginInvalid,
});
const validateHarmonizedSystemField = (value) => validateHarmonizedSystemCode(value, {
  invalidMessage: l10n.harmonizedSystemCodeInvalid,
});

const WEIGHT_OPTIONS = buildWeightSelectOptions();

const EMPTY_VALUE = {};

const validateNumeric = (v) => (
  v !== '' && Number.isNaN(Number(v))
    ? { isValid: false, errorMessage: localization.PRODUCT_INFO.validation.enterValidNumber }
    : { isValid: true }
);

const Shipping = ({
  requiresShipping,
  currentValue,
  countryCodeOfOrigin,
  harmonizedSystemCode,
  showComplianceFields,
  hideWeight,
  diff,
  detailsPanelVisibility,
  toggleVisibility,
  blockName,
  blockTitle,
  onChangeRequiresShipping,
  onChangeWeight,
  onChangeCountryCodeOfOrigin,
  onChangeHarmonizedSystemCode,
  readOnly,
  readOnlyTooltip,
}) => {
  const weightUnit = useMemo(
    () => normalizeWeightUnit(currentValue?.unit),
    [currentValue?.unit],
  );
  const { localValue, setLocalValue } = useLocalFieldValue(currentValue?.value || 0);

  const commitWeight = useCallback((v) => {
    onChangeWeight({ value: Number(v), unit: weightUnit });
  }, [onChangeWeight, weightUnit]);

  const {
    handleBlur,
    error,
    clearErrorIfValid,
  } = useBlurCommit({
    value: currentValue?.value || 0,
    localValue,
    setLocalValue,
    onCommit: commitWeight,
    serialize: String,
    validate: validateNumeric,
  });

  const handleChangeRequiresShipping = useCallback(() => {
    if (readOnly) return;
    onChangeRequiresShipping(!requiresShipping);
  }, [requiresShipping, onChangeRequiresShipping, readOnly]);

  const handleChangeWeight = useCallback((event) => {
    if (readOnly) return;
    setLocalValue(event.target.value);
    clearErrorIfValid(event.target.value);
  }, [readOnly, setLocalValue, clearErrorIfValid]);

  const saveWeight = useCallback(() => {
    if (readOnly) return;
    handleBlur();
  }, [readOnly, handleBlur]);

  const handleChangeUnit = useCallback((event) => {
    if (readOnly) return;
    onChangeWeight({ value: Number(localValue), unit: event.target.value });
  }, [localValue, onChangeWeight, readOnly]);

  const renderUnitValue = useCallback((value) => {
    const normalizedUnit = normalizeWeightUnit(value);
    const item = WEIGHT_OPTIONS.find(({ id }) => id === normalizedUnit);
    return item?.value || getUnitDisplayLabel('weight', normalizedUnit);
  }, []);

  return (
    <DetailsBlock
      dataQa="productShipping"
      detailsPanelVisibility={detailsPanelVisibility}
      toggleVisibility={toggleVisibility}
      blockName={blockName}
      blockTitle={blockTitle}
    >
      <ReadOnlyFieldTooltip tooltip={readOnly ? readOnlyTooltip : null}>
        <div className={s.row}>
          <InputControlLabel
            control="checkbox"
            label={<Tooltip content={diff?.requiresShipping ? 'Unpublished changes' : null} placement="top"><span className={diff?.requiresShipping ? s.diffDot : undefined}>This is a physical product</span></Tooltip>}
            value={requiresShipping}
            disabled={readOnly}
            onChange={handleChangeRequiresShipping}
          />
        </div>
      </ReadOnlyFieldTooltip>
      <If condition={requiresShipping && !hideWeight}>
        <div className={s.row}>
          <ReadOnlyFieldTooltip tooltip={readOnly ? readOnlyTooltip : null}>
            <TextField
              type="number"
              value={localValue}
              label={<Tooltip content={diff?.weight ? 'Unpublished changes' : null} placement="top"><span className={diff?.weight ? s.diffDot : undefined}>Weight</span></Tooltip>}
              disabled={readOnly}
              error={Boolean(error)}
              helperText={error}
              inputProps={{ min: 0, step: 'any' }}
              onChange={handleChangeWeight}
              onBlur={saveWeight}
            />
          </ReadOnlyFieldTooltip>
          <ReadOnlyFieldTooltip tooltip={readOnly ? readOnlyTooltip : null}>
            <Select
              style={{ width: 50, height: 30 }}
              value={weightUnit}
              options={WEIGHT_OPTIONS}
              renderValue={renderUnitValue}
              dataId="id"
              dataValue="value"
              disabled={readOnly}
              onChange={handleChangeUnit}
            />
          </ReadOnlyFieldTooltip>
        </div>
      </If>
      <If condition={showComplianceFields}>
        <hr className={s.hr} />
        <div className={s.row}>
          <InventoryTextField
            value={countryCodeOfOrigin || ''}
            label={buildComplianceFieldLabel({
              label: l10n.countryCodeOfOrigin,
              diff: diff?.countryCodeOfOrigin,
              infoTooltip: l10n.countryCodeOfOriginTooltip,
            })}
            placeholder={l10n.countryCodeOfOriginPlaceholder}
            normalize={normalizeCountryCodeOfOrigin}
            validate={validateCountryField}
            readOnly={readOnly}
            readOnlyTooltip={readOnlyTooltip}
            onCommit={onChangeCountryCodeOfOrigin}
          />
          <InventoryTextField
            value={harmonizedSystemCode || ''}
            label={buildComplianceFieldLabel({
              label: l10n.harmonizedSystemCode,
              diff: diff?.harmonizedSystemCode,
              infoTooltip: l10n.harmonizedSystemCodeTooltip,
            })}
            placeholder={l10n.harmonizedSystemCodePlaceholder}
            normalize={normalizeHarmonizedSystemCode}
            validate={validateHarmonizedSystemField}
            readOnly={readOnly}
            readOnlyTooltip={readOnlyTooltip}
            onCommit={onChangeHarmonizedSystemCode}
          />
        </div>
      </If>
    </DetailsBlock>
  );
};

Shipping.propTypes = {
  requiresShipping: PropTypes.bool,
  currentValue: PropTypes.shape({
    value: PropTypes.number,
    unit: PropTypes.string,
  }),
  countryCodeOfOrigin: PropTypes.string,
  harmonizedSystemCode: PropTypes.string,
  showComplianceFields: PropTypes.bool,
  hideWeight: PropTypes.bool,
  diff: PropTypes.shape({
    weight: PropTypes.bool,
    requiresShipping: PropTypes.bool,
    countryCodeOfOrigin: PropTypes.bool,
    harmonizedSystemCode: PropTypes.bool,
  }),
  detailsPanelVisibility: PropTypes.objectOf(PropTypes.bool).isRequired,
  toggleVisibility: PropTypes.func.isRequired,
  blockName: PropTypes.string.isRequired,
  blockTitle: PropTypes.node.isRequired,
  readOnly: PropTypes.bool,
  readOnlyTooltip: PropTypes.string,
  onChangeRequiresShipping: PropTypes.func.isRequired,
  onChangeWeight: PropTypes.func.isRequired,
  onChangeCountryCodeOfOrigin: PropTypes.func,
  onChangeHarmonizedSystemCode: PropTypes.func,
};

Shipping.defaultProps = {
  requiresShipping: false,
  currentValue: EMPTY_VALUE,
  countryCodeOfOrigin: '',
  harmonizedSystemCode: '',
  showComplianceFields: false,
  hideWeight: false,
  diff: {},
  readOnly: false,
  readOnlyTooltip: null,
  onChangeCountryCodeOfOrigin: () => null,
  onChangeHarmonizedSystemCode: () => null,
};

export default memo(Shipping);
